import React from 'react'

import { DashboardFeatureFlagsProvider } from '@components/utils/DashboardFeatureFlags'
import { DashboardPermissionsProvider } from '@components/utils/DashboardPermissions'
import { useWebviewEventsBus } from '@contexts/WebviewEventsBusContext'
import { AppearanceProvider } from '@fourthwall/appearance'
import { AuthInitializedBoundary, AuthProvider, Protected } from '@fourthwall/auth'
import { ComponentsThemeProvider, ModalProvider, SuspenseFallback } from '@fourthwall/components'
import { SitesManagerGuardContainer } from '@fourthwall/module-sites-manager-guard'
import { onWebViewModalClose, onWebViewModalOpen } from '@fourthwall/utils'
import { ExperimentsProvider } from '@modules/App/providers/ExperimentsProvider'
import { FingerprintProvider } from '@modules/App/providers/FingerprintProvider'
import { EntriContextProvider } from '@modules/Dashboard/pages/Settings/pages/Domain/providers/Entri'

import Dashboard from '../Dashboard'
import { CurrentShopProvider } from '../ThemeEditorV2/providers/CurrentShopProvider'
import { ShopTimeProvider } from '../Timezone/providers/ShopTimeProvider'
import { API_CLIENTS } from './apiClients'
import { ForbiddenShopGuard } from './modules/forbiddenShopGuard'
import { IncorrectDomainGuard } from './modules/incorrectDomainGuard'
import { AdminModeProvider } from './providers/AdminModeProvider'
import { TagManagerProvider } from './providers/TagManagerProvider'
import { WebViewProvider } from './providers/WebViewProvider'

export const AppView = () => {
  const webviewEventBus = useWebviewEventsBus()

  const handleInitAuthSuccess: Parameters<typeof AuthProvider>[0]['onInitAuthSuccess'] = (
    keycloak,
  ) => {
    API_CLIENTS.forEach((client) => {
      client.addRequestInterceptor((config) => {
        keycloak.updateToken(5)

        return config
      })
    })
  }

  const handleAuthSuccess: Parameters<typeof AuthProvider>[0]['onAuthSuccess'] = (keycloak) => {
    webviewEventBus.dispatchEvent('onAuthSuccess')

    const { authenticated, token, tokenParsed } = keycloak

    if (authenticated && token && tokenParsed) {
      API_CLIENTS.forEach((client) => {
        // NOTE: `setAuthorizationHeader` needs to be called before `setUserRequest` to set the token properly.
        client.setAuthorizationHeader(token)
      })
    }
  }

  return (
    <AuthProvider onInitAuthSuccess={handleInitAuthSuccess} onAuthSuccess={handleAuthSuccess}>
      <WebViewProvider>
        <AppearanceProvider>
          <AuthInitializedBoundary element={<SuspenseFallback />}>
            <Protected>
              <ComponentsThemeProvider mode="solid">
                <AdminModeProvider>
                  <DashboardPermissionsProvider>
                    <SitesManagerGuardContainer>
                      <ForbiddenShopGuard>
                        <TagManagerProvider>
                          <DashboardFeatureFlagsProvider>
                            <CurrentShopProvider>
                              <ShopTimeProvider>
                                <ExperimentsProvider>
                                  <ModalProvider
                                    onModalOpen={onWebViewModalOpen}
                                    onModalClose={onWebViewModalClose}
                                  >
                                    <IncorrectDomainGuard>
                                      <EntriContextProvider>
                                        <FingerprintProvider>
                                          <Dashboard />
                                        </FingerprintProvider>
                                      </EntriContextProvider>
                                    </IncorrectDomainGuard>
                                  </ModalProvider>
                                </ExperimentsProvider>
                              </ShopTimeProvider>
                            </CurrentShopProvider>
                          </DashboardFeatureFlagsProvider>
                        </TagManagerProvider>
                      </ForbiddenShopGuard>
                    </SitesManagerGuardContainer>
                  </DashboardPermissionsProvider>
                </AdminModeProvider>
              </ComponentsThemeProvider>
            </Protected>
          </AuthInitializedBoundary>
        </AppearanceProvider>
      </WebViewProvider>
    </AuthProvider>
  )
}
